import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Home } from 'lucide-react'


interface BreadcrumbItem {
  label: string
  path?: string
}

/**
 * Breadcrumb navigation component
 * Automatically generates breadcrumbs based on current route
 */
export const Breadcrumbs: React.FC = () => {
  const { t } = useTranslation('common')
  const location = useLocation()

  // Generate breadcrumb items from pathname
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const breadcrumbs: BreadcrumbItem[] = []

    // Always include home
    breadcrumbs.push({
      label: t('navigation.dashboard'),
      path: '/',
    })

    // Build breadcrumbs from path segments
    let currentPath = ''
    pathSegments.forEach((segment) => {
      currentPath += `/${segment}`
      
      // Map path segments to labels
      let label = segment
      switch (segment) {
        case 'finance':
          label = t('navigation.finance')
          break
        case 'hr':
          label = t('navigation.hr')
          break
        case 'employees':
          label = t('navigation.employees')
          break
        case 'customers':
          label = t('navigation.customers')
          break
        case 'quotations':
          label = t('navigation.quotations')
          break
        case 'invoices':
          label = t('navigation.invoices')
          break
        case 'new':
          label = t('actions.add')
          break
        case 'edit':
          label = t('actions.edit')
          break
        default:
          // For dynamic segments (IDs), keep as is or try to format
          if (/^[a-f0-9-]{36}$/.test(segment)) {
            // UUID format - show as ID
            label = `ID: ${segment.substring(0, 8)}`
          } else {
            // Capitalize first letter
            label = segment.charAt(0).toUpperCase() + segment.slice(1)
          }
      }

      breadcrumbs.push({
        label,
        path: currentPath,
      })
    })

    return breadcrumbs
  }

  const breadcrumbs = generateBreadcrumbs()

  if (breadcrumbs.length <= 1) {
    return null
  }

  return (
    <nav className="flex items-center space-x-1 text-sm text-muted-foreground">
      <Home className="w-4 h-4" />
      
      {breadcrumbs.map((breadcrumb, index) => {
        const isLast = index === breadcrumbs.length - 1
        
        return (
          <React.Fragment key={breadcrumb.path || breadcrumb.label}>
            <ChevronRight className="w-4 h-4 mx-1" />
            
            {isLast ? (
              <span className="text-foreground font-medium">
                {breadcrumb.label}
              </span>
            ) : breadcrumb.path ? (
              <Link
                to={breadcrumb.path}
                className="hover:text-foreground transition-colors"
              >
                {breadcrumb.label}
              </Link>
            ) : (
              <span>{breadcrumb.label}</span>
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}
